import { Component } from '@theme/component';

/**
 * dm-ugc-gallery — dot indicators for the snap-scroll customer photo track.
 *
 * The scrolling itself is native scroll-snap (which is also the reduced-motion
 * behaviour, per Figma 213:817) — this component only mirrors scroll position into the
 * dots. Purely decorative: dots are aria-hidden and the track is the focusable region.
 *
 * Dots exist only while the track actually overflows (scrollWidth > clientWidth + 1).
 * A row that fits — three tiles at 1440, the tablet grid — gets no dots, and a
 * ResizeObserver re-checks whenever the track's size changes.
 */
class DmUgcGallery extends Component {
  requiredRefs = ['track'];

  /** @type {number} */
  #raf = 0;

  #onScroll = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#sync());
  };

  /** @type {{ down: boolean, startX: number, startLeft: number }} */
  #drag = { down: false, startX: 0, startLeft: 0 };

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#buildDots();
    const track = this.refs.track;
    track?.addEventListener('scroll', this.#onScroll, { passive: true });

    if (track instanceof HTMLElement && 'ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#buildDots();
        this.#sync();
      });
      this.#resizeObserver.observe(track);
    }

    // Desktop drag-to-scroll (Figma 213:784). Touch keeps native momentum.
    if (track instanceof HTMLElement && window.matchMedia?.('(pointer: fine)').matches) {
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.#drag = { down: true, startX: event.clientX, startLeft: track.scrollLeft };
        track.dataset.dragging = 'true';
        track.setPointerCapture(event.pointerId);
      });
      track.addEventListener('pointermove', (event) => {
        if (!this.#drag.down) return;
        track.scrollLeft = this.#drag.startLeft - (event.clientX - this.#drag.startX);
      });
      const end = (/** @type {PointerEvent} */ event) => {
        if (!this.#drag.down) return;
        this.#drag.down = false;
        delete track.dataset.dragging;
        try { track.releasePointerCapture(event.pointerId); } catch { /* already released */ }
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
    }

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    cancelAnimationFrame(this.#raf);
  }

  #items() {
    return Array.from(this.refs.track?.querySelectorAll('.dm-ugc__item') || []);
  }

  /** True when the track has content beyond its visible width. */
  #scrollable() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return false;
    return track.scrollWidth > track.clientWidth + 1;
  }

  #buildDots() {
    const dots = this.refs.dots;
    if (!(dots instanceof HTMLElement)) return;

    const items = this.#items();
    if (!this.#scrollable()) {
      dots.hidden = true;
      if (dots.childElementCount) dots.replaceChildren();
      return;
    }

    dots.hidden = false;
    if (dots.childElementCount === items.length) return;

    dots.replaceChildren(
      ...items.map(() => {
        const dot = document.createElement('span');
        dot.className = 'dm-ugc__dot';
        return dot;
      })
    );
  }

  #sync() {
    const track = this.refs.track;
    const dots = this.refs.dots;
    if (!(track instanceof HTMLElement) || !(dots instanceof HTMLElement)) return;

    const items = this.#items();
    if (!items.length || !dots.childElementCount) return;

    const center = track.scrollLeft + track.clientWidth / 2;
    let active = 0;
    let best = Infinity;

    items.forEach((item, index) => {
      const element = /** @type {HTMLElement} */ (item);
      const mid = element.offsetLeft + element.offsetWidth / 2;
      const distance = Math.abs(mid - center);
      if (distance < best) {
        best = distance;
        active = index;
      }
    });

    Array.from(dots.children).forEach((dot, index) => {
      /** @type {HTMLElement} */ (dot).dataset.active = String(index === active);
    });
  }
}

if (!customElements.get('dm-ugc-gallery')) {
  customElements.define('dm-ugc-gallery', DmUgcGallery);
}
